import type { BaseQueryApi, FetchBaseQueryError, QueryReturnValue } from '@reduxjs/toolkit/query'
import type { RootState } from '@/store'
import { expectedReadIdOf } from '@/features/moderation/lib/expectedReadId'
import { refetchCourseModeration } from '@/features/moderation/lib/refetchCourseModeration'
import type { CourseWithoutBlocks } from '@/types/course'
import { sortByOrder } from './orderUtils'

type Base = (arg: string) => Q | PromiseLike<Q>
type Q = QueryReturnValue<unknown, FetchBaseQueryError, unknown>
type Result = { data: CourseWithoutBlocks } | { error: FetchBaseQueryError }

/**
 * Чтение курса редактором: ЧЕРНОВИК, если он есть, иначе живой курс. Ключ кеша — живой id.
 * Тело `getEditorCourse.queryFn` (см. CLAUDE.md, «Черновик живого курса»).
 */
export const readEditorCourse = async (
  liveId: number,
  { getState, dispatch }: Pick<BaseQueryApi, 'getState' | 'dispatch'>,
  baseQuery: Base,
): Promise<Result> => {
  const draftIdOf = () => expectedReadIdOf(getState() as RootState, liveId)
  let readId = draftIdOf()
  let res = await baseQuery(`gamified-courses/${readId}`)
  // 404 черновика на открытии: кеш модерации (до ~60 с) назвал черновик, который уже
  // слит или отменён. Перечитываем его и повторяем ОДИН раз; отказ повтора — как есть.
  if (res.error?.status === 404 && readId !== liveId) {
    await refetchCourseModeration(dispatch, () => getState() as RootState, liveId)
    readId = draftIdOf()
    res = await baseQuery(`gamified-courses/${readId}`)
  }
  if (res.error) return { error: res.error }
  const raw = res.data as CourseWithoutBlocks
  // `draftOfId` — только у ссылки на сам черновик: прочитанный НАМЕРЕННО черновик несёт
  // `draftOfId = liveId`, и редактор уводил бы на тот же адрес под вечным спиннером.
  // Код (`slug`) черновика наружу не выходит: по нему ученикам 404. Ссылки берут код
  // живого курса сами (`useLiveCourseSlug`).
  const view = { ...raw, id: liveId, readId, lessons: sortByOrder(raw.lessons) }
  const own = readId === liveId
  return { data: { ...view, slug: own ? raw.slug : null, draftOfId: own ? raw.draftOfId : null } }
}
